import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { trackEvent, EVENTS } from "@/lib/analytics";

// Primary geometric button with teal→charcoal hover fill.
// `track` = "primary" | "secondary" fires the matching GA4 CTA event with the button label.
export function Button({ as = "button", to, href, children, variant = "primary", className = "", track, onClick, ...props }) {
  const base =
    "group relative inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm tracking-wideish font-medium transition-colors duration-500 overflow-hidden";
  const variants = {
    primary: "bg-teal text-paper hover:bg-charcoal",
    ghost: "bg-transparent text-charcoal border border-charcoal/20 hover:border-teal hover:text-teal",
    light: "bg-paper text-charcoal hover:bg-teal hover:text-paper",
    dark: "bg-transparent text-paper border border-paper/25 hover:bg-paper hover:text-charcoal",
  };
  const cls = `${base} ${variants[variant]} ${className}`;
  const handleClick = (e) => {
    if (track) {
      trackEvent(track === "primary" ? EVENTS.ctaPrimary : EVENTS.ctaSecondary, {
        cta_label: typeof children === "string" ? children : undefined,
        cta_location: window.location.pathname,
      });
    }
    onClick?.(e);
  };
  const inner = (
    <>
      <span className="relative z-10">{children}</span>
      <ArrowUpRight className="relative z-10 h-4 w-4 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} aria-hidden="true" />
    </>
  );
  if (to) return <Link to={to} className={cls} onClick={handleClick} {...props}>{inner}</Link>;
  if (href) return <a href={href} className={cls} onClick={handleClick} {...props}>{inner}</a>;
  const Tag = as;
  return <Tag className={cls} onClick={handleClick} {...props}>{inner}</Tag>;
}

// Eyebrow label with teal tick. Default is small caps; `plain` renders sentence case.
export function Eyebrow({ children, dark = false, plain = false, className = "" }) {
  const tone = dark ? "text-teal-light" : "text-teal-deep";
  const type = plain ? "text-sm tracking-wideish" : "text-xs uppercase tracking-mega";
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <span className="h-px w-8 bg-teal" />
      <span className={`${type} ${tone}`}>{children}</span>
    </div>
  );
}
